/**
 * Customer Service Status Component
 * Shows green pulsing circle when service is open, stars when closed
 * Open hours: Monday-Thursday 8-16, Friday 8-14 (Danish time)
 */

class CustomerServiceStatus extends HTMLElement {
  constructor() {
    super();
    this.openText = this.dataset.openText || 'Vi sender ordrer lige nu';
    this.closedText = this.dataset.closedText || '';
    this.openLink = this.dataset.openLink || '';
    this.closedLink = this.dataset.closedLink || '';
    this.openLinkNewTab = this.dataset.openLinkNewTab === 'true';
    this.closedLinkNewTab = this.dataset.closedLinkNewTab === 'true';
    this.init();
  }

  connectedCallback() {
    // Update every minute to keep status accurate
    this.updateInterval = setInterval(() => {
      this.updateStatus();
    }, 60000); // 60 seconds
  }

  disconnectedCallback() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  init() {
    this.updateStatus();
  }

  /**
   * Check if customer service is currently open based on Danish time
   */
  isServiceOpen() {
    // Get current time in Danish timezone (Europe/Copenhagen)
    const now = new Date();
    const denmarkTime = new Intl.DateTimeFormat('da-DK', {
      timeZone: 'Europe/Copenhagen',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      weekday: 'short'
    });
    
    const parts = denmarkTime.formatToParts(now);
    const dayName = parts.find(p => p.type === 'weekday').value;
    const hour = parseInt(parts.find(p => p.type === 'hour').value);
    const minute = parseInt(parts.find(p => p.type === 'minute').value);
    
    // Map Danish day names to day numbers (1 = Monday, 5 = Friday, etc.)
    const dayMap = {
      'ma': 1, 'man': 1, // Monday
      'ti': 2, 'tir': 2, // Tuesday
      'on': 3, 'ons': 3, // Wednesday
      'to': 4, 'tor': 4, // Thursday
      'fr': 5, 'fre': 5  // Friday
    };
    
    const dayLower = dayName.toLowerCase();
    let dayNumber = 0;
    
    // Find matching day
    for (const [key, value] of Object.entries(dayMap)) {
      if (dayLower.startsWith(key)) {
        dayNumber = value;
        break;
      }
    }
    
    // Not a workday (Saturday/Sunday or unknown)
    if (dayNumber === 0) {
      return false;
    }
    
    const currentMinutes = hour * 60 + minute;
    
    // Monday to Thursday: 8:00 - 16:00
    if (dayNumber >= 1 && dayNumber <= 4) {
      return currentMinutes >= 480 && currentMinutes < 960; // 8:00 - 16:00
    }

    // Friday: 8:00 - 14:00
    if (dayNumber === 5) {
      return currentMinutes >= 480 && currentMinutes < 840; // 8:00 - 14:00
    }
    
    return false;
  }

  /**
   * Update the display based on current service status
   */
  updateStatus() {
    const isOpen = this.isServiceOpen();
    
    if (isOpen) {
      this.showOpenStatus();
    } else {
      this.showClosedStatus();
    }
  }

  /**
   * Show open status with green pulsing circle
   */
  showOpenStatus() {
    const content = `
      <div class="customer-service-status__indicator">
        <span class="customer-service-status__pulse"></span>
        <span class="customer-service-status__dot"></span>
      </div>
      <span class="customer-service-status__text">${this.openText}</span>
    `;
    
    if (this.openLink) {
      const targetAttr = this.openLinkNewTab ? 'target="_blank" rel="noopener noreferrer"' : '';
      this.innerHTML = `
        <a href="${this.openLink}" ${targetAttr} class="customer-service-status customer-service-status--open customer-service-status--link">
          ${content}
        </a>
      `;
    } else {
      this.innerHTML = `
        <div class="customer-service-status customer-service-status--open">
          ${content}
        </div>
      `;
    }
  }

  /**
   * Show closed status - text link if closedText is set, otherwise Trustpilot-like stars
   */
  showClosedStatus() {
    let content;

    if (this.closedText) {
      content = `<span class="customer-service-status__text">${this.closedText}</span>`;
    } else {
      const stars = Array(5).fill('').map(() => `
        <svg class="customer-service-status__star" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      `).join('');
      content = `<div class="customer-service-status__stars">${stars}</div>`;
    }

    if (this.closedLink) {
      const targetAttr = this.closedLinkNewTab ? 'target="_blank" rel="noopener noreferrer"' : '';
      this.innerHTML = `
        <a href="${this.closedLink}" ${targetAttr} class="customer-service-status customer-service-status--closed customer-service-status--link">
          ${content}
        </a>
      `;
    } else {
      this.innerHTML = `
        <div class="customer-service-status customer-service-status--closed">
          ${content}
        </div>
      `;
    }
  }
}

customElements.define('customer-service-status', CustomerServiceStatus);
